import { useMemo } from "react";

import { capitalize } from "../../../../../utils";
import { ChartData } from "../../../../../widget";

interface UseDealsClosedProps {
  dealsClosedChartData: ChartData;
}

interface UseDealsClosedReturnData {
  orderedEntries: {
    key: string;
    value: number;
    normalizedKey: string;
  }[];
  total: number;
  formatStatusLabel: (label: string) => string;
}

const PREFERRED_ORDER = ["approved", "rejected", "cancelled"];

const formatStatusLabel = (label: string): string =>
  label
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => capitalize(word))
    .join(" ");

export const useDealsClosed = ({ dealsClosedChartData }: UseDealsClosedProps): UseDealsClosedReturnData => {
  const entries = Object.entries(dealsClosedChartData ?? {}).map(([key, value]) => ({
    key,
    value,
    normalizedKey: key.trim().toLowerCase(),
  }));

  const total = useMemo(() => entries.reduce((sum, item) => sum + item.value, 0), [entries]);

  const orderedEntries = useMemo(() => {
    const remaining = [...entries];
    const ordered: typeof entries = [];

    PREFERRED_ORDER.forEach((status) => {
      const matchIndex = remaining.findIndex((item) => item.normalizedKey === status);
      if (matchIndex !== -1) {
        ordered.push(remaining[matchIndex]);
        remaining.splice(matchIndex, 1);
      } else {
        ordered.push({ key: status, value: 0, normalizedKey: status });
      }
    });

    return [...ordered, ...remaining];
  }, [entries]);

  return {
    orderedEntries,
    total,
    formatStatusLabel,
  };
};
